import React, { ReactNode, useContext, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  COOKIES_OTHER_APP_PROVISION,
  WEBSOCKET_RESPONSE_TYPE,
} from "../../utils/constants";
import { BidsContext } from "../../context/BidsContext";
import { ChatsContext } from "../../context/ChatContext";
import useNotification from "../../hooks/useNotification";
import usePageTracking from "../../hooks/usePageTracking";
import { Message } from "../../models/Chat";
import { DashboardContext } from "../../context/DashboardContext";
import { LoadingState } from "../../utils/enum";
import { cookies, useSession } from "../../shared/session";

interface Props {
  children?: ReactNode;
}

function PageTracker() {
    usePageTracking();
    return null;
}

// App-specific behaviour around the routes. The session itself (token from
// ?token= or the cookie, signature check, silent refresh, expiry, redirect to
// sign-in) is <SessionProvider>'s: this only renders once the user is signed in.
const AppWrapper: React.FC<Props> = ({ children }) => {
  const location = useLocation();
  const { status } = useSession();
  const signedIn = status === "signedIn";
  const { getUserCountryCode } = useContext(BidsContext);
  const {
    webSocket,
    selectedChat,
    chats,
    companyID,
    tabNotificationCount,
    setTabNotificationCount,
    setChats,
    handleChatArhived,
    getActiveChats,
    handleIncomingAllChats,
    readMessages,
    handleIncomingNewMessage,
    handleNewChatCreated,
    handleIncomingReadNotif,
  } = useContext(ChatsContext);
  const { setLoadingState, removeLoad } = useContext(DashboardContext);
  const { requestNotificationPermission, showNotification } = useNotification();

  // opened from the TMU provisioning flow of another app
  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    if (queryParams.get("fromProvisionTMU") === "true") {
      cookies.set(COOKIES_OTHER_APP_PROVISION, "true");
    }
  }, []);

  useEffect(() => {
    getUserCountryCode();
    requestNotificationPermission();
  }, []);

  const handleNewMessage = (payload: any) => {
    const isOnChatPage = location.pathname === "/chats";
    const { chatID } = payload.data;
    const { content, messageID, timestamp, senderID } = payload.data.message;

    handleIncomingNewMessage(payload);

    // ignore if message is from self
    if (senderID === companyID) return;

    // if on the chat page of that chat, mark as read
    if (isOnChatPage && selectedChat?.id === chatID) {
      readMessages([{ messageID, timestamp }]);
      // const audio = new Audio("/sounds/new-message.mp3");
      // audio.play();
    } else {
      // notify user if tab inactive or different chat selected
      showNotification({
        title: "New chat message",
        body: content,
        chatID,
      });

      // Increment tab notification count
      if (!isOnChatPage) {
        setTabNotificationCount(tabNotificationCount + 1);
      }
    }
  };

  const handleMissedMessages = (payload: any) => {
    const isOnChatPage = location.pathname === "/chats";
    const newData = payload.data; // { chatID: [messages] }
    const updatedChats = [...chats];

    Object.keys(newData).forEach((chatID) => {
      const index = chats.findIndex((chat) => chat.id === chatID);
      if (index >= 0) {
        updatedChats[index].messages.push(...newData[chatID]);
      }
      // notify about unread messages if not on that chat
      const unreadCount = newData[chatID].filter(
        (m: Message) => !m.readers.includes(companyID!)
      ).length;
      if (unreadCount > 0 && (!isOnChatPage || selectedChat?.id !== chatID)) {
        showNotification({
          title: "New messages",
          body: `You have ${unreadCount} new messages`,
          chatID,
          soundUrl: "/sounds/unread-message.mp3",
        });
      }
      // Mark as read if user is on that chat page
      // if (isOnChatPage && selectedChat?.id === chatID) {
      //   newData[chatID].forEach((msg: Message) => {
      //     if (!msg.readers.includes(companyID!)) {
      //       readMessages([{ messageID: msg.id, timestamp }]);
      //     }
      //   });
      // }
    });
    setChats(updatedChats);
  };

  const handleAllChatMessages = (payload: any) => {
    const isOnChatPage = location.pathname === "/chats";
    const allChats = handleIncomingAllChats(payload);
    if (!isOnChatPage) {
      let totalUnread = 0;
      allChats.forEach((chat) => {
        totalUnread += chat.messages.filter(
          (m) => !m.readers.includes(companyID!)
        ).length;
      });
      if (totalUnread > 0) {
        showNotification({
          title: "New Load messages",
          body: `You have ${totalUnread} unread messages across all load related chats`,
        });
        setTabNotificationCount(totalUnread);
      }
    }
  };

  // load chats only once the session is established
  useEffect(() => {
    if (companyID && signedIn) getActiveChats();
  }, [companyID, signedIn]);

  // Handle Chats..
  useEffect(() => {
    console.log("data", { webSocket, companyID, chats });
    if (webSocket && companyID) {
      webSocket.onmessage = (event: any) => {
        console.log("received event from AppWrapper", event);

        const payload = JSON.parse(event.data);
        console.log("received payload from AppWrapper", payload);

        switch (payload.type) {
          case WEBSOCKET_RESPONSE_TYPE.newMessage:
            handleNewMessage(payload);
            break;

          case WEBSOCKET_RESPONSE_TYPE.chatArchived:
            const loadNumID = handleChatArhived(payload);
            if (loadNumID) removeLoad(loadNumID);
            showNotification({
              title: "Load completed",
              body: ` ${
                loadNumID
                  ? `Chat for Load with ID: ${loadNumID}`
                  : "A chat thread"
              } has been archived as the load was completed.`,
            });
            break;

          case WEBSOCKET_RESPONSE_TYPE.newChatCreated:
            handleNewChatCreated(payload);
            setLoadingState(LoadingState.None);
            break;

          case WEBSOCKET_RESPONSE_TYPE.newChatMessages:
            handleMissedMessages(payload);
            break;

          case WEBSOCKET_RESPONSE_TYPE.allChatMessages:
            handleAllChatMessages(payload);
            break;

          case WEBSOCKET_RESPONSE_TYPE.readMessage:
            handleIncomingReadNotif(payload);
            break;
          default:
            break;
        }
      };
    }
  }, [webSocket, companyID, chats, selectedChat]);

  return <>
        {signedIn && <PageTracker/>}
        {children}
    </>;
};

export default AppWrapper;
